import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Eye, Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import RichTextEditor from "@/admin/components/RichTextEditor";
import MediaPicker from "@/admin/components/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Blog", "News", "Product Spotlight", "Supplier Insights"];
const ALL_TAGS = [
  "Blog",
  "News",
  "Product Spotlight",
  "Supplier Insights",
  "Events",
  "Advance ERP",
  "Aether eCommerce",
  "Integrated Partner",
  "Case Study",
  "Antera Academy",
];

const EMPTY_POST = {
  slug: "",
  title: "",
  excerpt: "",
  body_html: "",
  category: "Blog",
  tags: [],
  date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
  image: "",
  author: "Antera Team",
  read_time: "3 min read",
  published: true,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export default function AdminBlogEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug || slug === "new";

  const [form, setForm] = useState(EMPTY_POST);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [insertImageResolver, setInsertImageResolver] = useState(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/blog/posts/${slug}`);
        setForm({
          slug: data.slug,
          title: data.title || "",
          excerpt: data.excerpt || "",
          body_html: data.body_html || "",
          category: data.category || "Blog",
          tags: data.tags || [],
          date: data.date || "",
          image: data.image || "",
          author: data.author || "Antera Team",
          read_time: data.read_time || "",
          published: data.published !== false,
        });
      } catch {
        toast.error("Post not found.");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleTag = (tag) => {
    setForm((prev) => {
      const has = prev.tags.includes(tag);
      return { ...prev, tags: has ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag] };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (isNew && !form.slug.trim()) {
      toast.error("Slug is required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form, slug: form.slug || slugify(form.title) };
        await axios.post(`${API}/admin/blog/posts`, payload);
        toast.success("Post created.");
        navigate(`/admin/blog/${payload.slug}`);
      } else {
        const { slug: _, ...patch } = form;
        await axios.patch(`${API}/admin/blog/posts/${slug}`, patch);
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Inline image picker for the RichText editor's image button.
  const handlePickImageFromEditor = () =>
    new Promise((resolve) => {
      setInsertImageResolver(() => resolve);
      setImagePickerOpen(true);
    });

  const handleMediaPicked = (url) => {
    if (insertImageResolver) {
      insertImageResolver(url);
      setInsertImageResolver(null);
      setImagePickerOpen(false);
      return;
    }
    // hero image picker
    update({ image: url });
    setImagePickerOpen(false);
  };

  const handleMediaClose = () => {
    if (insertImageResolver) {
      insertImageResolver(null);
      setInsertImageResolver(null);
    }
    setImagePickerOpen(false);
  };

  if (loading) {
    return (
      <div className="p-10">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-edit-page" className="bg-slate-50 min-h-full">
      <Toaster position="top-right" richColors />

      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-5 lg:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/admin/blog"
            data-testid="admin-blog-edit-back"
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded hover:bg-slate-100"
            title="Back to list"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <p className="text-xs text-slate-400 font-mono truncate">
              {isNew ? "/admin/blog/new" : `/blog/${form.slug}`}
            </p>
            <p className="text-sm font-bold text-slate-900 truncate">
              {form.title || (isNew ? "New post" : "Untitled")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isNew && (
            <a
              href={`/blog/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-sky-600 inline-flex items-center gap-1"
            >
              <Eye className="h-3 w-3" /> Preview
            </a>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-blog-save"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-7 max-w-6xl mx-auto">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-5">
            <Field label="Title">
              <Input
                value={form.title}
                onChange={(e) => {
                  update({ title: e.target.value });
                  if (isNew && (!form.slug || form.slug === slugify(form.title))) {
                    update({ slug: slugify(e.target.value) });
                  }
                }}
                placeholder="An attention-grabbing title…"
                className="text-base font-semibold"
                data-testid="admin-blog-title"
              />
            </Field>

            {isNew && (
              <Field label="Slug" hint="URL-friendly identifier. Used in /blog/<slug>. Permanent after save.">
                <Input
                  value={form.slug}
                  onChange={(e) => update({ slug: slugify(e.target.value) })}
                  placeholder="my-awesome-post"
                  className="font-mono text-xs"
                  data-testid="admin-blog-slug"
                />
              </Field>
            )}

            <Field label="Excerpt" hint="Used on listing cards and SEO descriptions.">
              <Textarea
                value={form.excerpt}
                onChange={(e) => update({ excerpt: e.target.value })}
                placeholder="One or two short sentences summarizing the post."
                rows={3}
                className="text-sm"
                data-testid="admin-blog-excerpt"
              />
            </Field>

            <Field label="Body">
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => update({ body_html: html })}
                placeholder="Tell the story…"
                onPickImage={handlePickImageFromEditor}
                testId="admin-blog-body"
              />
            </Field>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Publishing</p>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">Published</span>
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => update({ published: e.target.checked })}
                  data-testid="admin-blog-published"
                  className="w-4 h-4 accent-sky-600"
                />
              </label>
              <p className="text-[10px] text-slate-400 mt-2">
                {form.published ? "Visible on the live site." : "Saved as a draft."}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Hero Image</p>
              {form.image ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-200">
                  <img src={form.image} alt="" className="w-full h-32 object-cover" />
                  <button
                    type="button"
                    onClick={() => update({ image: "" })}
                    className="absolute top-2 right-2 bg-white/90 text-slate-600 hover:text-red-500 rounded-full p-1 shadow"
                    aria-label="Remove image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center bg-slate-50/60">
                  <ImageIcon className="h-6 w-6 text-slate-300 mx-auto mb-1" />
                  <p className="text-[11px] text-slate-400">No image set</p>
                </div>
              )}
              <Button
                type="button"
                onClick={() => {
                  setInsertImageResolver(null);
                  setImagePickerOpen(true);
                }}
                data-testid="admin-blog-pick-image"
                variant="outline"
                className="w-full h-9 text-xs rounded-lg"
              >
                {form.image ? "Replace image" : "Pick from library"}
              </Button>
              <Input
                value={form.image}
                onChange={(e) => update({ image: e.target.value })}
                placeholder="https://… or leave blank"
                className="text-xs font-mono"
                data-testid="admin-blog-image-url"
              />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Category</p>
              <select
                value={form.category}
                onChange={(e) => update({ category: e.target.value })}
                data-testid="admin-blog-category"
                className="w-full h-9 rounded-lg border border-slate-200 text-sm px-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tags</p>
              <p className="text-[10px] text-slate-400 leading-relaxed">
                Tags drive which pages this post appears on (e.g. Advance ERP shows on /advance).
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_TAGS.map((t) => {
                  const active = form.tags.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      data-testid={`admin-blog-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Meta</p>
              <Field label="Date" tight>
                <Input
                  value={form.date}
                  onChange={(e) => update({ date: e.target.value })}
                  placeholder="May 18, 2026"
                  className="text-xs"
                  data-testid="admin-blog-date"
                />
              </Field>
              <Field label="Author" tight>
                <Input
                  value={form.author}
                  onChange={(e) => update({ author: e.target.value })}
                  placeholder="Antera Team"
                  className="text-xs"
                  data-testid="admin-blog-author"
                />
              </Field>
              <Field label="Read time" tight>
                <Input
                  value={form.read_time}
                  onChange={(e) => update({ read_time: e.target.value })}
                  placeholder="3 min read"
                  className="text-xs"
                  data-testid="admin-blog-read-time"
                />
              </Field>
            </div>
          </aside>
        </div>
      </div>

      <MediaPicker
        open={imagePickerOpen}
        onClose={handleMediaClose}
        onPick={handleMediaPicked}
        mode="picker"
      />
    </div>
  );
}

function Field({ label, hint, children, tight = false }) {
  return (
    <div className={tight ? "" : "space-y-1.5"}>
      <Label className={`text-[10px] font-bold text-slate-500 uppercase tracking-widest ${tight ? "mb-1 block" : ""}`}>
        {label}
      </Label>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}
